import { z } from 'zod';
import { sortParam } from '../sort.js';
import { nullableDate } from '../common.js';
import { TAX_RATES } from '../offers/calc.js';
/**
 * Flotte (KON-167, siehe docs/flotte.md): Fahrzeuge mit Fristen (Hauptuntersuchung, Versicherung), Wartung, Fahrzeugkosten und ein Fahrtenbuch. Kilometer sind ganze Zahlen,
 * Beträge ganze Cent, Mengen (Liter, kWh) Tausendstel. Kosten und Anschaffungswerte sieht nur, wer das Recht `fleet.values` hat.
 */
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
const patchText = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
const km = (label) => z.number(`Bitte ${label} angeben`).int('Ganze Kilometer').min(0, 'Nicht negativ').max(10_000_000, 'Kilometerstand zu groß');
const taxRate = z.union(TAX_RATES.map((r) => z.literal(r)), 'Steuersatz 19, 7 oder 0 %');
const cents = z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(0, 'Nicht negativ').max(1_000_000_000_00);
const date = z.iso.date('Ungültiges Datum');
// ---------------------------------------------------------------------------
// Fahrzeuge
// ---------------------------------------------------------------------------
import { SERVICE_CLASSES } from '../service-classes.js';
export const VEHICLE_KINDS = ['car', 'van', 'truck', 'motorcycle', 'trailer', 'other'];
export const VEHICLE_KIND_LABELS = { car: 'Pkw', van: 'Transporter', truck: 'Lkw', motorcycle: 'Motorrad', trailer: 'Anhänger', other: 'Sonstiges' };
export const FUEL_TYPES = ['petrol', 'diesel', 'electric', 'hybrid', 'gas', 'none'];
export const FUEL_TYPE_LABELS = { petrol: 'Benzin', diesel: 'Diesel', electric: 'Elektro', hybrid: 'Hybrid', gas: 'Gas', none: 'Ohne Antrieb' };
export const VEHICLE_STATUSES = ['active', 'workshop', 'retired', 'sold'];
export const VEHICLE_STATUS_LABELS = { active: 'Im Einsatz', workshop: 'In der Werkstatt', retired: 'Stillgelegt', sold: 'Verkauft' };
/** Kennzeichen: Großbuchstaben, Leerzeichen vereinheitlicht („m ab 123“ → „M AB 123“) */
const plate = z
    .string()
    .trim()
    .min(2, 'Kennzeichen fehlt')
    .max(15, 'Höchstens 15 Zeichen')
    .transform((v) => v.toUpperCase().replace(/\s+/g, ' '));
const vin = z
    .string()
    .trim()
    .transform((v) => v.toUpperCase().replace(/\s+/g, ''))
    .refine((v) => v === '' || /^[A-HJ-NPR-Z0-9]{17}$/.test(v), 'Die Fahrzeug-Identifizierungsnummer hat 17 Zeichen (Buchstaben außer I, O, Q und Ziffern)')
    .nullable()
    .optional()
    .transform((v) => (v ? v : null));
const vehicleFields = {
    plate,
    name: text(100),
    kind: z.enum(VEHICLE_KINDS, 'Bitte die Art wählen').default('car'),
    manufacturer: text(100),
    model: text(100),
    color: text(40),
    /** Serviceklasse im Chauffeur-Betrieb, leer = nicht eingeordnet */
    serviceClass: z.enum(SERVICE_CLASSES, 'Bitte die Klasse wählen').nullable().optional(),
    /** Sitzplätze für Fahrgäste (ohne Fahrer) */
    passengerCapacity: z.number('Bitte die Plätze als Zahl angeben').int('Ganze Plätze').min(1, 'Mindestens 1 Platz').max(99, 'Höchstens 99 Plätze').nullable().optional(),
    vin,
    firstRegistration: nullableDate,
    fuel: z.enum(FUEL_TYPES, 'Bitte den Kraftstoff wählen').default('petrol'),
    /** Kilometerstand bei der Aufnahme in Wirkia, danach schreiben Fahrten, Wartung und Kosten ihn fort */
    odometerKm: km('den Kilometerstand').default(0),
    employeeId: z.uuid('Ungültiger Mitarbeiter').nullable().optional(),
    status: z.enum(VEHICLE_STATUSES).default('active'),
    /** Nächste Hauptuntersuchung (HU/AU) */
    huDueOn: nullableDate,
    insuranceUntil: nullableDate,
    insurer: text(100),
    acquiredOn: nullableDate,
    acquisitionGrossCents: cents.nullable().optional(),
    taxRate: taxRate.nullable().optional(),
    depreciationYears: z.number('Bitte die Jahre angeben').int('Ganze Jahre').min(1, 'Mindestens 1 Jahr').max(50, 'Höchstens 50 Jahre').nullable().optional(),
    note: text(2000),
};
export const createVehicleInput = z
    .object(vehicleFields)
    .strict()
    .superRefine((v, ctx) => {
    if (v.acquisitionGrossCents != null && v.taxRate == null)
        ctx.addIssue({ code: 'custom', path: ['taxRate'], message: 'Bitte den Steuersatz wählen' });
    if (v.status === 'sold')
        ctx.addIssue({ code: 'custom', path: ['status'], message: 'Ein neues Fahrzeug kann nicht verkauft sein' });
});
export const updateVehicleInput = z
    .object({
    plate: vehicleFields.plate.optional(),
    name: patchText(100),
    kind: z.enum(VEHICLE_KINDS).optional(),
    manufacturer: patchText(100),
    model: patchText(100),
    color: patchText(40),
    serviceClass: vehicleFields.serviceClass,
    passengerCapacity: vehicleFields.passengerCapacity,
    /** Kilometerstand korrigieren. Er darf nicht unter dem höchsten Stand aus Fahrtenbuch, Wartung und Kosten liegen. */
    odometerKm: km('den Kilometerstand').optional(),
    vin: z
        .string()
        .trim()
        .transform((v) => v.toUpperCase().replace(/\s+/g, ''))
        .refine((v) => v === '' || /^[A-HJ-NPR-Z0-9]{17}$/.test(v), 'Die Fahrzeug-Identifizierungsnummer hat 17 Zeichen (Buchstaben außer I, O, Q und Ziffern)')
        .nullable()
        .optional()
        .transform((v) => (v === undefined ? undefined : v ? v : null)),
    firstRegistration: nullableDate,
    fuel: z.enum(FUEL_TYPES).optional(),
    employeeId: vehicleFields.employeeId,
    status: z.enum(VEHICLE_STATUSES).optional(),
    huDueOn: nullableDate,
    insuranceUntil: nullableDate,
    insurer: patchText(100),
    acquiredOn: nullableDate,
    acquisitionGrossCents: vehicleFields.acquisitionGrossCents,
    taxRate: vehicleFields.taxRate,
    depreciationYears: vehicleFields.depreciationYears,
    note: patchText(2000),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listVehiclesQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().max(100).optional(),
    status: z.enum(VEHICLE_STATUSES).optional(),
    kind: z.enum(VEHICLE_KINDS).optional(),
    employeeId: z.uuid().optional(),
    /** Nur Fahrzeuge mit einer Frist, die in den nächsten 30 Tagen abläuft oder überschritten ist */
    due: z.enum(['1']).optional(),
    /** Nur freie oder nur im Einsatz befindliche Fahrzeuge (Fahrzeuge in der Werkstatt, stillgelegt oder verkauft zählen zu keinem von beiden) */
    availability: z.enum(['free', 'in_use']).optional(),
    sort: sortParam(['plate', 'name', 'status', 'km', 'huDueOn'], 'plate').optional(),
});
/** Wie ein Fahrzeug gerade dasteht: berechnet aus Einsätzen und Signalen, nie gespeichert. Werkstatt, stillgelegt und verkauft kommen vom Status des Fahrzeugs. */
export const VEHICLE_AVAILABILITIES = ['free', 'in_use', 'workshop', 'retired', 'sold'];
export const VEHICLE_AVAILABILITY_LABELS = { free: 'Frei', in_use: 'Im Einsatz', workshop: 'In der Werkstatt', retired: 'Stillgelegt', sold: 'Verkauft' };
// ---------------------------------------------------------------------------
// Wartung
// ---------------------------------------------------------------------------
export const MAINTENANCE_KINDS = ['service', 'hu', 'tires', 'repair', 'other'];
export const MAINTENANCE_KIND_LABELS = { service: 'Inspektion', hu: 'HU/AU (TÜV)', tires: 'Reifen', repair: 'Reparatur', other: 'Sonstiges' };
export const createMaintenanceInput = z
    .object({
    kind: z.enum(MAINTENANCE_KINDS, 'Bitte die Art wählen'),
    doneOn: date,
    odometerKm: km('den Kilometerstand').nullable().optional(),
    costGrossCents: cents.nullable().optional(),
    taxRate: taxRate.nullable().optional(),
    workshop: text(100),
    description: text(1000),
    /** Wann die nächste Wartung dieser Art fällig ist (Datum und/oder Kilometerstand) */
    nextDueOn: nullableDate,
    nextDueKm: km('den Kilometerstand').nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.costGrossCents != null && v.taxRate == null)
        ctx.addIssue({ code: 'custom', path: ['taxRate'], message: 'Bitte den Steuersatz wählen' });
});
// ---------------------------------------------------------------------------
// Kosten
// ---------------------------------------------------------------------------
export const COST_KINDS = ['fuel', 'charging', 'toll', 'parking', 'wash', 'insurance', 'tax', 'other'];
export const COST_KIND_LABELS = { fuel: 'Tanken', charging: 'Laden', toll: 'Maut', parking: 'Parken', wash: 'Wäsche', insurance: 'Versicherung', tax: 'Kfz-Steuer', other: 'Sonstiges' };
export const createCostInput = z
    .object({
    kind: z.enum(COST_KINDS, 'Bitte die Art wählen'),
    spentOn: date,
    odometerKm: km('den Kilometerstand').nullable().optional(),
    /** Liter oder kWh in Tausendsteln, bei Tanken und Laden */
    quantityMilli: z.number('Bitte die Menge angeben').int().min(1, 'Die Menge muss größer als 0 sein').max(10_000_000).nullable().optional(),
    amountGrossCents: cents.min(1, 'Bitte einen Betrag angeben'),
    taxRate: taxRate.default(19),
    note: text(500),
})
    .strict();
export const listEntriesQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    vehicleId: z.uuid().optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
});
// ---------------------------------------------------------------------------
// Fahrtenbuch
// ---------------------------------------------------------------------------
export const TRIP_PURPOSES = ['business', 'private', 'commute'];
export const TRIP_PURPOSE_LABELS = { business: 'Geschäftlich', private: 'Privat', commute: 'Wohnung und Arbeitsstätte' };
/**
 * Eine Fahrt im Fahrtenbuch. Für ein ordnungsgemäßes Fahrtenbuch (§ 6 Abs. 1 Nr. 4 EStG) gehören dazu: Datum, Kilometerstand zu Beginn und am Ende, Ziel und Zweck der Fahrt,
 * bei Geschäftsfahrten der Reiseanlass und der aufgesuchte Geschäftspartner. Eine spätere Änderung bleibt sichtbar (Vermerk „geändert“, Änderungsprotokoll).
 */
export const createTripInput = z
    .object({
    vehicleId: z.uuid('Bitte ein Fahrzeug wählen'),
    driverEmployeeId: z.uuid('Ungültiger Fahrer').nullable().optional(),
    tripDate: date,
    startKm: km('den Kilometerstand zu Beginn'),
    endKm: km('den Kilometerstand am Ende'),
    fromPlace: z.string().trim().min(1, 'Startort fehlt').max(150),
    toPlace: z.string().trim().min(1, 'Ziel fehlt').max(150),
    purpose: z.enum(TRIP_PURPOSES, 'Bitte den Zweck wählen'),
    contactId: z.uuid('Ungültiger Geschäftspartner').nullable().optional(),
    reason: text(300),
    note: text(500),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.endKm < v.startKm)
        ctx.addIssue({ code: 'custom', path: ['endKm'], message: 'Der Stand am Ende darf nicht unter dem Stand zu Beginn liegen' });
    if (v.purpose === 'business' && !v.reason && !v.contactId)
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Bei einer Geschäftsfahrt gehören der Reiseanlass oder der Geschäftspartner dazu' });
});
export const updateTripInput = z
    .object({
    driverEmployeeId: z.uuid('Ungültiger Fahrer').nullable().optional(),
    tripDate: date.optional(),
    startKm: km('den Kilometerstand zu Beginn').optional(),
    endKm: km('den Kilometerstand am Ende').optional(),
    fromPlace: z.string().trim().min(1, 'Startort fehlt').max(150).optional(),
    toPlace: z.string().trim().min(1, 'Ziel fehlt').max(150).optional(),
    purpose: z.enum(TRIP_PURPOSES).optional(),
    contactId: z.uuid('Ungültiger Geschäftspartner').nullable().optional(),
    reason: patchText(300),
    note: patchText(500),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listTripsQuery = listEntriesQuery.extend({
    driverEmployeeId: z.uuid().optional(),
    purpose: z.enum(TRIP_PURPOSES).optional(),
    q: z.string().trim().max(100).optional(),
    sort: sortParam(['tripDate', 'vehicle', 'km'], '-tripDate').optional(),
});
export const LOGBOOK_FORMATS = ['csv', 'xlsx', 'pdf'];
export const logbookExportQuery = z.object({ from: z.iso.date().optional(), to: z.iso.date().optional(), format: z.enum(LOGBOOK_FORMATS, 'Bitte csv, xlsx oder pdf wählen').default('csv') });
/** Lücken und Überschneidungen. Die Fahrten müssen nach Datum, Beginn-Kilometerstand und Anlage aufsteigend sortiert sein. */
export function logbookGaps(trips) {
    const gaps = [];
    for (let i = 1; i < trips.length; i++) {
        const prev = trips[i - 1];
        const next = trips[i];
        if (next.startKm > prev.endKm)
            gaps.push({ kind: 'gap', afterTripId: prev.id, beforeTripId: next.id, fromKm: prev.endKm, toKm: next.startKm, km: next.startKm - prev.endKm });
        else if (next.startKm < prev.endKm)
            gaps.push({ kind: 'overlap', afterTripId: prev.id, beforeTripId: next.id, fromKm: next.startKm, toKm: prev.endKm, km: prev.endKm - next.startKm });
    }
    return gaps;
}
/**
 * Restwert und Steuer eines Fahrzeugs: wie beim Inventar lineare Abschreibung, monatsgenau ab dem Anschaffungsmonat, vom Nettobetrag. Die steuerliche Nutzungsdauer von Pkw
 * beträgt nach der AfA-Tabelle in der Regel 6 Jahre. Die Rechnung steht in `assetValue` (inventory).
 */
/** Frist: überschritten, in den nächsten 30 Tagen oder in Ordnung */
export function deadlineState(dueOn, today, soonDays = 30) {
    if (!dueOn)
        return null;
    if (dueOn < today)
        return 'overdue';
    const soon = new Date(Date.parse(`${today}T12:00:00Z`) + soonDays * 86_400_000).toISOString().slice(0, 10);
    return dueOn <= soon ? 'soon' : 'ok';
}
// ---------------------------------------------------------------------------
// Einsätze und Signale
// ---------------------------------------------------------------------------
const wall = z.string().trim().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'Bitte Datum und Uhrzeit wie 2026-10-15T08:00 angeben');
/**
 * Ein Fahrzeug für einen Zeitraum einplanen, optional für einen Auftrag und mit einem Fahrer. Zeiten sind Wandzeit in Europe/Berlin (JJJJ-MM-TTTHH:MM). Solange der Zeitraum läuft,
 * gilt das Fahrzeug als „Im Einsatz“. Zwei Einsätze desselben Fahrzeugs dürfen sich nicht überschneiden.
 */
export const createAssignmentInput = z
    .object({
    vehicleId: z.uuid('Bitte ein Fahrzeug wählen'),
    orderId: z.uuid('Ungültiger Auftrag').nullable().optional(),
    employeeId: z.uuid('Ungültiger Mitarbeiter').nullable().optional(),
    startsAt: wall,
    endsAt: wall,
    note: text(500),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.endsAt <= v.startsAt)
        ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'Das Ende muss nach dem Beginn liegen' });
});
export const updateAssignmentInput = z
    .object({
    orderId: z.uuid('Ungültiger Auftrag').nullable().optional(),
    employeeId: z.uuid('Ungültiger Mitarbeiter').nullable().optional(),
    startsAt: wall.optional(),
    endsAt: wall.optional(),
    note: patchText(500),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listAssignmentsQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    vehicleId: z.uuid().optional(),
    orderId: z.uuid().optional(),
    employeeId: z.uuid().optional(),
    /** Nur laufende, nur künftige oder nur vergangene Einsätze */
    when: z.enum(['now', 'upcoming', 'past']).optional(),
});
export const SIGNAL_SOURCES = ['app', 'telematics', 'manual'];
export const SIGNAL_KINDS = ['position', 'trip_start', 'trip_end', 'status'];
/**
 * Signal eines Fahrzeugs, gedacht für die Mitarbeiter-App und Telematik: Position, Fahrtbeginn, Fahrtende oder Status. Mit `state` meldet es „Im Einsatz“ oder „Frei“ und bestimmt damit
 * den Status des Fahrzeugs mit, wenn kein Einsatz eingeplant ist. Der Zeitpunkt ist der Moment der Messung (ISO), nicht des Eintreffens.
 */
export const createSignalInput = z
    .object({
    employeeId: z.uuid('Ungültiger Mitarbeiter').nullable().optional(),
    source: z.enum(SIGNAL_SOURCES).default('app'),
    kind: z.enum(SIGNAL_KINDS, 'Bitte die Art des Signals angeben'),
    state: z.enum(['in_use', 'free']).nullable().optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
    accuracyM: z.number().int().min(0).max(100_000).nullable().optional(),
    speedKmh: z.number().min(0).max(500).nullable().optional(),
    odometerKm: z.number().int().min(0).max(10_000_000).nullable().optional(),
    recordedAt: z.iso.datetime({ offset: true }).optional(),
    payload: z.record(z.string(), z.unknown()).nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.latitude == null) !== (v.longitude == null))
        ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Breite und Länge gehören zusammen' });
    if (v.kind === 'trip_start' && v.state === 'free')
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'Ein Fahrtbeginn meldet „Im Einsatz“' });
    if (v.kind === 'trip_end' && v.state === 'in_use')
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'Ein Fahrtende meldet „Frei“' });
})
    .transform((v) => ({ ...v, state: v.state ?? (v.kind === 'trip_start' ? 'in_use' : v.kind === 'trip_end' ? 'free' : null) }));
/** So lange gilt ein Signal mit Status, wenn danach nichts Neues kommt (Stunden). Danach fällt das Fahrzeug auf „Frei“ zurück, damit eine vergessene Fahrt nicht ewig hängt. */
export const SIGNAL_VALID_HOURS = 12;
//# sourceMappingURL=fleet.js.map